import type { SupabaseClient } from '@supabase/supabase-js';

/** One generated result card. Legacy job states are filtered before display. */
export interface RecipeResult {
  id: string;
  recipeId: string;
  responseId: string;
  title: string;
  respondentName: string;
  /** Stable campaign response number, independent of result sorting or pagination. */
  responseNumber: number | null;
  content: string | null;
  status: 'pending' | 'running' | 'complete' | 'failed' | 'skipped';
  createdAt: string;
}
/** Read the campaign's single optional automatic recipe under the caller's RLS. */
export async function getCampaignAutoRecipe(
  supabase: SupabaseClient,
  sessionId: string,
): Promise<string | null> {
  const { data, error } = await supabase
    .from('campaign_auto_recipes')
    .select('recipe_id')
    .eq('session_id', sessionId)
    .maybeSingle();
  if (error) throw new Error('Could not load auto-run');
  return data?.recipe_id ?? null;
}
/** Save or clear explicit opt-in. The database checks role, plan, recipe and scope. */
export async function setCampaignAutoRecipe(
  supabase: SupabaseClient,
  sessionId: string,
  recipeId: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('set_campaign_auto_recipe', {
    p_session_id: sessionId,
    p_recipe_id: recipeId,
  });
  if (error) throw new Error('Could not update auto-run');
}
/** Query shapes stay local to this RLS-backed data layer. */
interface SavedResult {
  id: string;
  recipe_id: string;
  interview_data_id: string;
  content: string;
  generated_at: string;
}
/** Durable job metadata contains no provider errors or recipe instructions. */
interface RunResult {
  id: string;
  recipe_id: string;
  recipe_name: string;
  response_id: string;
  case_study_id: string | null;
  status: RecipeResult['status'];
  created_at: string;
}
/** Load saved HTML results; job metadata supplies names when a recipe is private. */
export async function listRecipeResults(
  supabase: SupabaseClient,
  sessionId: string,
  responseId?: string,
  limit = 20,
): Promise<RecipeResult[]> {
  let saved = supabase
    .from('case_studies_visible')
    .select('id,recipe_id,interview_data_id,content,generated_at')
    .eq('interview_session_id', sessionId)
    .eq('generation_status', 'complete')
    .in('content_type', ['case_study', 'expert_article', 'custom'])
    .not('recipe_id', 'is', null)
    .not('interview_data_id', 'is', null);
  let runs = supabase
    .from('recipe_auto_runs')
    .select('id,recipe_id,recipe_name,response_id,case_study_id,status,created_at')
    .eq('session_id', sessionId);
  if (responseId) {
    saved = saved.eq('interview_data_id', responseId);
    runs = runs.eq('response_id', responseId);
  }
  const [contentResult, runResult] = await Promise.all([
    saved.order('generated_at', { ascending: false }).limit(limit),
    runs.order('created_at', { ascending: false }).limit(limit),
  ]);
  if (contentResult.error || runResult.error) throw new Error('Could not load recipe results');
  const contents = (contentResult.data ?? []) as SavedResult[];
  const jobs = (runResult.data ?? []) as RunResult[];
  const recipeIds = [
    ...new Set([...contents.map((r) => r.recipe_id), ...jobs.map((r) => r.recipe_id)]),
  ];
  const responseIds = [
    ...new Set([...contents.map((r) => r.interview_data_id), ...jobs.map((r) => r.response_id)]),
  ];
  if (!recipeIds.length) return [];
  const [names, people] = await Promise.all([
    supabase.from('recipes').select('id,name').in('id', recipeIds),
    supabase
      .from('interview_data')
      .select('id,interviewee_name,response_number')
      .eq('interview_session_id', sessionId)
      .in('id', responseIds),
  ]);
  if (names.error || people.error) throw new Error('Could not load recipe results');
  const recipeNames = new Map<string, string>((names.data ?? []).map((r) => [r.id, r.name]));
  const respondents = new Map<string, { name: string; number: number | null }>(
    (people.data ?? []).map((r) => [r.id, {
      name: r.interviewee_name?.trim() || 'Anonymous',
      number: r.response_number ?? null,
    }]),
  );
  const cards: RecipeResult[] = contents.map((row) => ({
    id: row.id,
    recipeId: row.recipe_id,
    responseId: row.interview_data_id,
    title:
      recipeNames.get(row.recipe_id) ??
      jobs.find((j) => j.recipe_id === row.recipe_id)?.recipe_name ??
      'Recipe result',
    respondentName: respondents.get(row.interview_data_id)?.name ?? 'Anonymous',
    responseNumber: respondents.get(row.interview_data_id)?.number ?? null,
    content: row.content,
    status: 'complete',
    createdAt: row.generated_at,
  }));
  return cards
    .filter(card => Boolean(card.content?.trim()))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}
